import { clear, el, icon } from './dom.js';
import type { IconName } from './icons.js';
import type { Check } from './raceChecks.js';
import { plural } from '../i18n/i18n.js';

/**
 * The furniture both step-by-step dialogs share: the stepper along the top, the checklist of
 * what a step needs, and the banner that explains a refused Next.
 *
 * Shared because the two must behave identically or neither can be learned: the race wizard
 * and the tour builder are opened one on top of the other, and a checklist that went red in
 * one and silently disabled a button in the other would teach two contradictory lessons in
 * the same minute.
 */

export interface StepDef {
  id: string;
  label: string;
}

/** Numbered dots that turn into ticks; past steps are links back, later ones are not yet. */
export function paintStepper(
  list: HTMLElement,
  steps: StepDef[],
  current: number,
  onJump: (id: string) => void,
): void {
  clear(list);
  for (const [i, step] of steps.entries()) {
    const done = i < current;
    const button = el(
      'button',
      { type: 'button', class: 'stepper-button', dataset: { step: step.id } },
      el('span', { class: 'stepper-dot' }, done ? icon('check') : String(i + 1)),
      el('span', { class: 'stepper-label' }, step.label),
    );
    button.addEventListener('click', () => onJump(step.id));
    const item = el(
      'li',
      { class: `stepper-item${i === current ? ' is-current' : ''}${done ? ' is-done' : ''}` },
      button,
    );
    if (i === current) item.setAttribute('aria-current', 'step');
    list.append(item);
  }
}

/**
 * What a step needs, each item ticked off as it happens.
 *
 * An unmet requirement is a quiet open circle until somebody tries to go on without it —
 * then it turns red and grows its explanation. A warning is shown from the start, because it
 * never stops anybody and is only useful if read before the wait it warns about.
 */
export function paintChecklist(list: HTMLElement, checks: Check[], attempted: boolean): void {
  clear(list);
  for (const check of checks) {
    const failed = !check.ok && (attempted || check.level === 'warn');
    const state = check.ok ? 'is-ok' : failed ? (check.level === 'warn' ? 'is-warn' : 'is-error') : 'is-todo';
    const glyph: IconName = check.ok
      ? 'check_circle'
      : check.level === 'warn'
        ? 'warning'
        : failed
          ? 'error'
          : 'radio_button_unchecked';
    list.append(
      el(
        'li',
        { class: `checklist-item ${state}`, dataset: { check: check.id } },
        icon(glyph, 'checklist-icon'),
        el(
          'span',
          { class: 'checklist-text' },
          el('span', { class: 'checklist-label' }, check.label),
          failed && el('span', { class: 'checklist-problem' }, check.problem),
        ),
      ),
    );
  }
}

/**
 * The banner beside the buttons, for a refused Next.
 *
 * A summary, not a second copy: what is missing, by name, with the instructions left where
 * they already are — on the red items in the list above, next to the thing they are about.
 */
export function paintBanner(banner: HTMLElement, checks: Check[], attempted: boolean): Check | null {
  const blockers = attempted ? checks.filter((check) => !check.ok && check.level === 'block') : [];
  banner.classList.toggle('is-hidden', !blockers.length);
  clear(banner);
  if (blockers.length) {
    banner.append(
      icon('error', 'banner-icon'),
      el(
        'div',
        { class: 'banner-text' },
        el('strong', {}, plural(blockers.length, 'One thing left before you can continue', '{n} things left before you can continue')),
        el(
          'p',
          {},
          plural(blockers.length, '{labels} — see the red item above.', '{labels} — see the red items above.', {
            labels: blockers.map((b) => b.label).join(', '),
          }),
        ),
      ),
    );
  }
  return blockers[0] ?? null;
}

/** Restarts the banner's nudge, so a second refusal of the same thing is still visible. */
export function nudge(node: HTMLElement): void {
  node.classList.remove('is-nudged');
  void node.offsetWidth;
  node.classList.add('is-nudged');
}
